import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { AssessmentService } from "./shared/services/assessment.service";
import { UserService } from "../shared/services/user.service";
import { Credential, CYD } from "./shared/models/credential";
import { ViewStateService } from "../shared/services/view-state.service";
import { Assessment } from "./shared/models/assessment";
import { NotificationsService } from "../shared/services/notifications.service";
import { PractitionerService } from "../practitioner/shared/services/practitioner.service";
import { of, Subject } from "rxjs";
import { catchError, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "app-assessment",
  templateUrl: "./assessment.component.html",
  styleUrls: ["./assessment.component.css"],
})
export class AssessmentComponent implements OnInit, OnDestroy {
  personId: string;
  cyd: CYD;
  credentials: Array<Credential>;
  credential: Credential;
  selectedCredential: string;
  status: string;
  subjectToCMP: boolean;
  crceOnlyText =
    "*CRCE credit only; not to reduce CEUs documented for the NBRC";
  isUnderNBRCJE = false;
  onDestroy$ = new Subject();

  constructor(
    private assessmentService: AssessmentService,
    private userService: UserService,
    private router: Router,
    private viewState: ViewStateService,
    private notifications: NotificationsService
  ) {
    this.credentials = new Array<Credential>();
    this.credential = new Credential();
    this.cyd = new CYD();
  }

  ngOnInit() {
    this.initializeComponent();
  }

  private initializeComponent() {
    this.personId = this.userService.CurrentUser.personId;
    this.status = this.userService.CurrentUser.status;
    this.subjectToCMP = this.userService.CurrentUser.subjectToCMP;
    this.isUnderNBRCJE = this.userService.CurrentUser.underNBRCJE;
    this.assessmentService.destroyQuestion();
    this.assessmentService.setAssessment(new Assessment());
    this.loadDashboard();
  }

  startAssessment(assessment: Assessment) {
    this.router.navigate([
      "assessment/" + assessment.assessmentId + "/terms-and-conditions",
    ]);
  }

  reviewResult(assessment: Assessment) {
    this.router.navigate([
      "assessment/" + assessment.assessmentId + "/result-review/1",
    ]);
  }

  activateAssessment() {
    this.viewState.startLoading();
    this.assessmentService
      .updateOptin(this.personId, { isOptIn: true })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (status) => {
          this.viewState.stopLoading();
          this.initializeComponent(); // Call the common initialization logic
        },
        (errors) => {
          this.handleServerErrorResponse(errors);
        }
      );
  }

  private loadDashboard() {
    this.viewState.startLoading();
    this.assessmentService
    .fetchDashboardAssessments(this.personId)
    .pipe(
      takeUntil(this.onDestroy$),
      tap((res) => {
        if (res) {
          this.cyd = res;
          this.credentials = res.credentials;
          if (this.credentials.length === 1) {
            this.selectedCredential = this.credentials[0].name;
            this.credential = this.filterAssessments(this.selectedCredential);
          }
          this.viewState.stopLoading();
        }
      }),
      catchError((error) => {
        this.handleServerErrorResponse(error); 
        return of(null);
      })
    )
    .subscribe();
  }

  selectCredential(credentialName) {
    this.credential = this.filterAssessments(credentialName);
  }

  private filterAssessments(name) {
    return this.credentials.filter((credential) => {
      return credential.name === name;
    })[0];
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500)
      {this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred. Please try again or contact NBRC for assistance."
      );}
    else {
      const error = response;
      this.notifications.error("Error", error[0].message);
    }
    this.viewState.stopLoading();
  }

  canReview(assessment: Assessment) {
    if (assessment.opensOn && assessment.closesOn) {
      const d1 = new Date(assessment.opensOn);
      const d2 = new Date(assessment.closesOn);
      return new Date() >= d1 && new Date() >= d2;
    }
    return false;
  }

  isAvailable(assessment: Assessment) {
    if (assessment.opensOn && assessment.closesOn) {
      const d1 = new Date(assessment.opensOn);
      const d2 = new Date(assessment.closesOn);
      return new Date() >= d1 && new Date() <= d2;
    }
    return false;
  }

  willBeAvailable(assessment: Assessment) {
    if (assessment.opensOn) {
      const d1 = new Date(assessment.opensOn);
      return d1 >= new Date();
    }
    return false;
  }

  isExpired(assessment: Assessment) {
    if (assessment.closesOn) {
      const d2 = new Date(assessment.closesOn);
      return new Date() >= d2;
    }
    return false;
  }

  isCredentialExpired() {
    if (!this.cyd.ceuDueOn) {
      //Null
      return false;
    } else {
      const d2 = new Date(this.cyd.ceuDueOn);
      return new Date() >= d2;
    }
  }

  blockCopy() {
    this.assessmentService
      .warnUser(this.personId, "copy")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (res) => {
          this.notifications.errorWithWait(
            "Warning",
            "Your key strokes are recorded by the system. Efforts to capture content could initiate an investigation of potential misconduct by the NBRC Judicial and Ethics Committee.",
            0
          );
        },
        (err) => {
          console.log(err);
        }
      );
  }

  blockCut() {
    this.assessmentService
      .warnUser(this.personId, "cut")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (res) => {
          this.notifications.errorWithWait(
            "Warning",
            "Your key strokes are recorded by the system. Efforts to capture content could initiate an investigation of potential misconduct by the NBRC Judicial and Ethics Committee.",
            0
          );
        },
        (err) => {
          console.log(err);
        }
      );
  }
  
  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
